// Estado inicial del flow de aporte de producto. Dos CTAs apiladas
// verticalmente (uno arriba, otro abajo) — no en fila, porque en el
// panel lateral desktop el ancho es limitado y compartir el espacio
// entre dos botones full-width los achica demasiado.
//
// Los productos usados en la última generación ya no tienen un CTA aparte:
// aparecen primeros y con badge "Último utilizado" dentro de "Elegir de
// mis productos" (ver PickProductPanel).

import React from 'react';
import { Upload } from 'lucide-react';
import { useUserProducts, useUserProductsLoaded } from '../productsContext';
import type { PresetV2 } from '../mockData';
import CachedImg from '../../../components/CachedImg';

export interface ChooseProductPanelProps {
  onShowProducts: () => void;
  /** "Subir nuevo producto" — NO abre el file picker directo: pasa primero
   *  por la pantalla explicativa (estado vacío de UploadProductPanel) donde
   *  se ve cómo subir el producto y recién ahí se elige la foto. */
  onStartNewProduct: () => void;
  /** Kind del preset abierto — controla la copy gris (el texto cambia
   *  para que sea consistente con el modo: template/design hablan de
   *  "preset", design-modelo de "elegir modelo / pose / fondo",
   *  design-auto de "armar la escena automáticamente"). */
  presetKind?: PresetV2['kind'];
}

export const ChooseProductPanel: React.FC<ChooseProductPanelProps> = ({
  onShowProducts,
  onStartNewProduct,
  presetKind,
}) => {
  // Productos del user — si no tiene ninguno, ocultamos el CTA "Elegir de
  // mis productos" (no tendría qué mostrar). Los 3 más recientes (la query
  // viene ordenada por created_at desc) se usan como thumbnails apilados en
  // ese botón, en lugar del ícono de caja.
  const products = useUserProducts();
  const productsLoaded = useUserProductsLoaded();
  const recentThumbs = products.slice(0, 3);
  // Mostramos el CTA "Elegir de mis productos" de forma OPTIMISTA: mientras
  // el catálogo todavía carga (`!productsLoaded`) lo dejamos visible para que
  // el botón no aparezca "tarde" al abrir el modo. Solo lo ocultamos si tras
  // cargar confirmamos que el user no tiene productos. Los thumbnails entran
  // cuando resuelve el fetch.
  const showPickButton = !productsLoaded || products.length > 0;

  return (
    <div>
      <div className="flex items-start gap-3.5 mb-6">
        <div className="w-11 h-11 rounded-xl bg-brand-primary/10 flex items-center justify-center flex-shrink-0">
          <Upload className="w-5 h-5 text-brand-primary" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[16px] font-semibold text-brand-black/90 leading-tight">
            Elegí el producto para generar
          </p>
          <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
            {presetKind === 'design-auto'
              ? 'Atlas va a usar esta foto y armar la escena entera con IA — sin configurar nada más.'
              : presetKind === 'design-modelo'
                ? 'Atlas va a usar esta foto y combinarla con el modelo, la pose y el fondo que elijas.'
                : 'Atlas va a usar esta foto para aplicar el preset elegido y armar la imagen final.'}
          </p>
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        <button
          type="button"
          onClick={onStartNewProduct}
          className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[15.5px] font-semibold shadow-sm transition-colors"
        >
          <Upload className="w-5 h-5" />
          Subir nuevo producto
        </button>
        {showPickButton && (
          <button
            type="button"
            onClick={onShowProducts}
            className="w-full inline-flex items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[15.5px] font-semibold transition-colors"
          >
            {/* Stack horizontal de los 3 productos más recientes — uno al
                lado del otro, solapados. El más reciente queda al frente
                (izquierda). Reemplaza el ícono de caja. */}
            <span className="flex shrink-0 -space-x-2.5">
              {recentThumbs.map((p, i) => (
                <span
                  key={p.id}
                  className="relative w-7 h-7 rounded-lg overflow-hidden bg-neutral-100 ring-2 ring-white"
                  style={{ zIndex: recentThumbs.length - i }}
                >
                  <CachedImg
                    src={p.imageUrl}
                    alt={p.name}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                </span>
              ))}
            </span>
            Elegir de mis productos
          </button>
        )}
      </div>
    </div>
  );
};
